// Quick-action chips above the composer (text motions, video motion, review scene) + the tekno3 scene
// review (spec 2026-10-10 §9). The chips send a canned chat message; "Review scene" runs the review in place
// — never a popup: issues by severity (click → seek + select the box), the marked frames (click → seek), Apply all / per issue (one undo step each), Undo, Re-check.
// Every async outcome is a visible line in the card.
import React, { useEffect, useRef, useState } from "react";
import { useComposer, useComposerRuntime, useThread } from "@assistant-ui/react";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import i18n from "../../../locales/i18n";
import { getStore } from "../../../common/environment_config";
import { selectBox } from "../../../common/actions";
import { seekEditorPlayhead } from "../../../common/playhead_tracker";
import { applyCourseOps } from "../../utils/applyCourseOps";
import { requestReview } from "./deepagentReview";

const t = (k, o) => i18n.t(`aideaChat.tekno3.${k}`, o);
const MOTION_CHIPS = [
  ["chipTextMotions", "chipTextMotionsPrompt"],
  ["chipVideoMotion", "chipVideoMotionPrompt"],
];
const RANK = { high: 0, med: 1, low: 2 };
const CATEGORIES = new Set([
  "contrast",
  "overflow",
  "safe_area",
  "overlap",
  "min_font",
  "reading_speed",
  "bible",
  "timeline",
  "media",
  "hierarchy",
  "layout",
  "typography",
  "colour_contrast",
  "imagery",
  "motion_timing",
  "content_fit",
  "accessibility",
]);
// A typed message naming tekno3 with a review verb ("review this scene with tekno3", "tekno3 ile incele") starts a review.
export const TEKNO3_RE =
  /\b(review|check|incele|kontrol|gözden|revisa|revisar)\b[\s\S]*\btekno ?3\b|\btekno ?3\b[\s\S]*\b(review|check|incele|kontrol|gözden|revisa|revisar)\b/i;
const secs = (v) => (Number(v) || 0).toFixed(1);
const rank = (issue) => RANK[issue.severity] ?? 3;

/** The op that fixes one issue: its own suggestion, else a proposed fix on the same box. */
export function fixFor(issue, fixes) {
  return (
    issue.suggestedOp || (issue.boxId && fixes.find((op) => op && op.boxId === issue.boxId)) || null
  );
}

// Newest user message as one comparable string (useThread selectors must return primitives).
function lastUserText(s) {
  const users = (s.messages || []).filter((m) => m.role === "user");
  const m = users[users.length - 1];
  if (!m) return "";
  const text = (m.content || [])
    .filter((p) => p && p.type === "text")
    .map((p) => p.text)
    .join(" ");
  return `${m.id}\u0000${text}`;
}

export default function ReviewTekno3Card({ navId }) {
  const scene = /^pa-/.test(navId || "");
  const [phase, setPhase] = useState("idle"); // idle | running | ready | error
  const [report, setReport] = useState(null);
  const [error, setError] = useState("failed");
  const [result, setResult] = useState(null); // { applied, skipped } of the last Apply
  const [note, setNote] = useState(null); // "undone" | "undoStale"
  const [done, setDone] = useState(() => new Set()); // indexes of issues already fixed
  const [allApplied, setAllApplied] = useState(false); // Apply all ran — ops without a boxId never mark an issue done
  const [undoAt, setUndoAt] = useState(null); // { top, ids } of the last Apply: its history entry + the issues it fixed
  const busy = useRef(false);
  const composer = useComposerRuntime();
  const draft = useComposer((c) => c.text).trim(); // a typed draft must not be overwritten by a chip
  const sending = useThread((s) => !!s.isRunning);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = async (quick) => {
    const store = getStore();
    if (!scene || !store || busy.current) return;
    busy.current = true;
    setPhase("running");
    setResult(null);
    setNote(null);
    setDone(new Set());
    setAllApplied(false);
    setUndoAt(null);
    const out = (await requestReview(
      { trainingId: window.idealStudioTrainingId, navId, quick },
      store.getState
    )) || { error: "failed" };
    busy.current = false;
    if (!mounted.current) return;
    if (out.error) {
      setError(out.error);
      setPhase("error");
      return;
    }
    setReport({ ...out, issues: out.issues.filter((x) => x && typeof x === "object") });
    setPhase("ready");
  };

  // ponytail: a thread switch whose newest user message names tekno3 also starts a review — rare, harmless.
  const lastUser = useThread(lastUserText);
  const seen = useRef(lastUser);
  useEffect(() => {
    if (lastUser === seen.current) return;
    seen.current = lastUser;
    if (TEKNO3_RE.test(lastUser.slice(lastUser.indexOf("\u0000") + 1))) run(false);
  }, [lastUser]); // ponytail: run reads the current props; re-binding it would re-fire on every render

  const apply = (ops, indexes) => {
    const store = getStore();
    if (!store || !ops.length) return false;
    const topOf = () => {
      const { past } = store.getState().undoGroup;
      return past[past.length - 1];
    };
    const before = topOf();
    const out = applyCourseOps(store.dispatch, store.getState, ops, { navId });
    const top = topOf();
    // only the issues whose op really applied are done — a failed Fix keeps its button
    const ids = indexes.filter((i) =>
      out.applied.some((a) => a.index === ops.indexOf(fixFor(report.issues[i], report.fixes)))
    );
    setUndoAt(out.applied.length && top !== before ? { top, ids } : null);
    setResult({ applied: out.applied.length, skipped: out.errors.length });
    setNote(null);
    setDone((d) => new Set([...d, ...ids]));
    return out.applied.length > 0;
  };

  // Undo only while the Apply's history entry is still the newest — never the user's later edit.
  const undo = () => {
    const store = getStore();
    if (!store) return;
    const { past } = store.getState().undoGroup;
    if (past[past.length - 1] !== undoAt.top) {
      setUndoAt(null);
      setNote("undoStale");
      return;
    }
    store.dispatch(UndoActionCreators.undo());
    setDone((d) => new Set([...d].filter((i) => !undoAt.ids.includes(i))));
    setAllApplied(false);
    setUndoAt(null);
    setResult(null);
    setNote("undone");
  };

  const focus = async (boxId, sec) => {
    await seekEditorPlayhead(navId, sec); // opens the scene first if needed (resets the selection)
    const store = getStore();
    const box = boxId && store && store.getState().undoGroup.present.boxesById[boxId];
    if (box && box.parent === navId) store.dispatch(selectBox(boxId, box));
  };

  if (!scene) return null;
  const ghost = "aidea-build-btn aidea-build-btn-ghost";
  const close = (
    <button type="button" className={ghost} onClick={() => setPhase("idle")}>
      {t("close")}
    </button>
  );

  const send = (text) => {
    composer.setText(text);
    composer.send();
  };
  const chips = (
    <div className="aidea-t3-quick">
      {MOTION_CHIPS.map(([label, prompt]) => (
        <button
          key={label}
          type="button"
          className={ghost}
          disabled={sending || !!draft}
          title={draft ? t("composerNotEmpty") : undefined}
          onClick={() => send(t(prompt))}
        >
          {t(label)}
        </button>
      ))}
      <button
        type="button"
        className={ghost}
        disabled={sending || phase === "running"}
        onClick={() => run(false)}
      >
        {t("quick")}
      </button>
    </div>
  );

  if (phase === "idle") return chips;
  if (phase === "running") {
    return (
      <>
        {chips}
        <div className="aidea-build-notice" role="status">
          {t("running")}
        </div>
      </>
    );
  }
  if (phase === "error") {
    return (
      <>
        {chips}
        <div className="aidea-build-notice" role="status">
          <div role="alert" className="aidea-build-notice-error">
            {t(`err.${error}`)}
          </div>
          <div className="aidea-build-notice-actions">
            <button type="button" className="aidea-build-btn" onClick={() => run(false)}>
              {t("retry")}
            </button>
            {close}
          </div>
        </div>
      </>
    );
  }

  const { issues, fixes, frames, summary } = report;
  const shots = frames.filter((f) => f && typeof f.pngBase64 === "string");
  const order = issues
    .map((issue, i) => ({ issue, i }))
    .sort((a, b) => rank(a.issue) - rank(b.issue));
  const allOps = fixes.length ? fixes : issues.map((x) => x.suggestedOp).filter(Boolean);
  const applyAll = () => {
    if (
      apply(
        allOps,
        issues.map((x, i) => (fixFor(x, fixes) ? i : -1)).filter((i) => i >= 0)
      )
    )
      setAllApplied(true);
  };

  return (
    <>
      {chips}
      <div className="aidea-build-notice aidea-t3" role="status">
        <div>{issues.length ? t("found", { count: issues.length }) : t("clean")}</div>
        {summary && <div className="aidea-build-notice-line">{String(summary).slice(0, 400)}</div>}
        {shots.length > 0 && (
          <div className="aidea-t3-frames">
            {shots.map((f, n) => (
              <button
                key={`${f.t}-${n}`}
                type="button"
                className="aidea-t3-frame"
                aria-label={t("frameAlt", { sec: secs(f.t) })}
                onClick={() => focus(null, f.t)}
              >
                <img alt="" src={`data:image/png;base64,${f.pngBase64}`} />
              </button>
            ))}
          </div>
        )}
        {order.length > 0 && (
          <ul className="aidea-t3-issues">
            {order.map(({ issue, i }) => {
              const op = fixFor(issue, fixes);
              const sev = RANK[issue.severity] !== undefined ? issue.severity : "low";
              const cat = CATEGORIES.has(issue.category) ? issue.category : "other";
              return (
                <li key={issue.id || i} data-severity={sev}>
                  <button
                    type="button"
                    className="aidea-t3-issue"
                    onClick={() => focus(issue.boxId, issue.t)}
                  >
                    <span className="aidea-t3-sev">{t(`severity.${sev}`)}</span> ·{" "}
                    {t(`category.${cat}`)} · {t("at", { sec: secs(issue.t) })}
                    {issue.evidence && (
                      <span className="aidea-t3-evidence">
                        {String(issue.evidence).slice(0, 200)}
                      </span>
                    )}
                  </button>
                  {op && !done.has(i) && (
                    <button
                      type="button"
                      className="aidea-build-btn"
                      onClick={() => apply([op], [i])}
                    >
                      {t("fix")}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {result && (
          <div className="aidea-build-notice-line">
            {t("applied", { count: result.applied })}
            {result.skipped ? ` ${t("skipped", { count: result.skipped })}` : ""}
          </div>
        )}
        {note && <div className="aidea-build-notice-line">{t(note)}</div>}
        <div className="aidea-build-notice-actions">
          {allOps.length > 0 && done.size === 0 && !allApplied && (
            <button type="button" className="aidea-build-btn" onClick={applyAll}>
              {t("applyAll", { count: allOps.length })}
            </button>
          )}
          {undoAt !== null && (
            <button type="button" className={ghost} onClick={undo}>
              {t("undo")}
            </button>
          )}
          <button type="button" className={ghost} onClick={() => run(true)}>
            {t("recheck")}
          </button>
          {close}
        </div>
      </div>
    </>
  );
}
